import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { PLYLoader } from "three/examples/jsm/loaders/PLYLoader.js";
import { heightmapUrlFor, previewUrlFor, fileUrlFor, fetchAuthedBlob, fetchAuthedBuffer } from "./api";

export default function Scene3D({ dataset, exaggeration }) {
  const mountRef = useRef(null);
  const stateRef = useRef({});
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState("");

  // One-time scene init.
  useEffect(() => {
    const mount = mountRef.current;
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    renderer.setClearColor(0x050807, 1);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog(0x050807, 800, 2500);

    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 10000);
    camera.position.set(0, 380, 600);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.06;
    controls.minDistance = 50;
    controls.maxDistance = 2200;
    controls.maxPolarAngle = Math.PI * 0.485;

    // Lights.
    scene.add(new THREE.AmbientLight(0xa5b8b3, 0.6));
    const dir = new THREE.DirectionalLight(0xffffff, 1.4);
    dir.position.set(-400, 600, 300);
    scene.add(dir);
    const rim = new THREE.DirectionalLight(0x3dc0af, 0.5);
    rim.position.set(500, 200, -500);
    scene.add(rim);

    // Reference grid.
    const grid = new THREE.GridHelper(1500, 30, 0x1c2a27, 0x0d1817);
    grid.position.y = -0.5;
    scene.add(grid);

    const sizeMount = () => {
      const { clientWidth: w, clientHeight: h } = mount;
      renderer.setSize(w, h, false);
      camera.aspect = w / Math.max(1, h);
      camera.updateProjectionMatrix();
    };
    sizeMount();
    const ro = new ResizeObserver(sizeMount);
    ro.observe(mount);

    let stopped = false;
    const animate = () => {
      if (stopped) return;
      controls.update();
      renderer.render(scene, camera);
      requestAnimationFrame(animate);
    };
    animate();

    stateRef.current = { renderer, scene, camera, controls, ro, dir, contentGroup: null };

    return () => {
      stopped = true;
      ro.disconnect();
      controls.dispose();
      renderer.dispose();
      mount.removeChild(renderer.domElement);
    };
  }, []);

  // Load content when dataset changes.
  useEffect(() => {
    const { scene, camera, controls } = stateRef.current;
    if (!scene || !dataset) return;
    let cancelled = false;
    setLoading(true);
    setInfo("");

    // Remove previous content.
    if (stateRef.current.contentGroup) {
      scene.remove(stateRef.current.contentGroup);
      stateRef.current.contentGroup.traverse((o) => {
        if (o.geometry) o.geometry.dispose();
        if (o.material) {
          if (o.material.map) o.material.map.dispose();
          o.material.dispose();
        }
      });
      stateRef.current.contentGroup = null;
    }

    const group = new THREE.Group();
    scene.add(group);
    stateRef.current.contentGroup = group;

    (async () => {
      try {
        if (dataset.kind === "geotiff") {
          await loadGeoTiffTerrain(group, dataset, exaggeration);
          // Frame camera.
          const box = new THREE.Box3().setFromObject(group);
          frameCamera(camera, controls, box);
          setInfo(`Terrain · ${dataset.elevation_min?.toFixed?.(1)}–${dataset.elevation_max?.toFixed?.(1)} m`);
        } else {
          const fileUrl = fileUrlFor(dataset.id);
          const ext = dataset.file_ext;
          if (ext === ".obj") {
            const text = await (await fetch(fileUrl, { headers: authHeaders() })).text();
            const obj = new OBJLoader().parse(text);
            applyDefaultMaterial(obj);
            group.add(obj);
            setInfo(`Mesh · OBJ`);
          } else if (ext === ".ply") {
            const buf = await fetchAuthedBuffer(fileUrl);
            const geom = new PLYLoader().parse(buf);
            geom.computeBoundingBox();
            if (geom.index && geom.index.count > 0) {
              if (!geom.attributes.normal) geom.computeVertexNormals();
              const mesh = new THREE.Mesh(
                geom,
                new THREE.MeshStandardMaterial({
                  color: 0xc9d4d2, side: THREE.DoubleSide,
                  vertexColors: !!geom.attributes.color,
                  metalness: 0.05, roughness: 0.85,
                })
              );
              group.add(mesh);
              setInfo(`Mesh · PLY`);
            } else {
              const points = new THREE.Points(
                geom,
                new THREE.PointsMaterial({
                  size: 1.4,
                  vertexColors: !!geom.attributes.color,
                  color: 0x3dc0af, sizeAttenuation: true,
                })
              );
              group.add(points);
              setInfo(`Point Cloud · PLY · ${geom.attributes.position.count.toLocaleString()} pts`);
            }
          }
          // Frame and center.
          const box = new THREE.Box3().setFromObject(group);
          const center = box.getCenter(new THREE.Vector3());
          group.position.sub(center);
          const newBox = new THREE.Box3().setFromObject(group);
          frameCamera(camera, controls, newBox);
        }
      } catch (err) {
        console.error("3D load failed", err);
        setInfo(`Load failed: ${err.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataset?.id]);

  // Update terrain exaggeration without reloading.
  useEffect(() => {
    const grp = stateRef.current?.contentGroup;
    if (!grp || !grp.userData.heightmapData) return;
    applyHeightmapDisplacement(grp.userData.heightmapData, exaggeration);
  }, [exaggeration]);

  return (
    <div ref={mountRef} style={{ width: "100%", height: "100%", position: "relative" }} data-testid="viewer-scene-3d">
      {loading && (
        <div className="tbg-vw-empty">
          <div className="t">Loading</div>
          <div className="s">{dataset?.name || ""}</div>
        </div>
      )}
      {info && !loading && (
        <div className="tbg-vw-hud tl">
          <div className="chip" data-testid="viewer-3d-info">
            <span className="k">3D</span>
            <span className="v">{info}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function authHeaders() {
  const t = localStorage.getItem("tbg_viewer_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function applyDefaultMaterial(obj) {
  obj.traverse((c) => {
    if (c.isMesh) {
      c.material = new THREE.MeshStandardMaterial({
        color: 0xc9d4d2, metalness: 0.05, roughness: 0.85, side: THREE.DoubleSide,
      });
    }
  });
}

function frameCamera(camera, controls, box) {
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z) || 100;
  const dist = maxDim * 1.6;
  camera.position.set(center.x + dist * 0.7, center.y + dist * 0.7, center.z + dist * 0.9);
  controls.target.copy(center);
  controls.update();
}

async function loadGeoTiffTerrain(group, dataset, exaggeration) {
  // Fetch both PNGs with auth.
  const [heightBlobUrl, previewBlobUrl] = await Promise.all([
    fetchAuthedBlob(heightmapUrlFor(dataset.id)),
    fetchAuthedBlob(previewUrlFor(dataset.id)),
  ]);

  // Decode heightmap pixels.
  const heightImg = await loadImage(heightBlobUrl);
  const W = heightImg.naturalWidth;
  const H = heightImg.naturalHeight;
  const segW = Math.min(W - 1, 255);
  const segH = Math.min(H - 1, 255);
  const heights = sampleHeights(heightImg, segW + 1, segH + 1);

  // Compute plane size from geographic bounds.
  const [w, s, e, n] = dataset.bounds || [-1, -1, 1, 1];
  const meanLat = (s + n) / 2;
  const widthM = (e - w) * 111320 * Math.cos((meanLat * Math.PI) / 180);
  const heightM = (n - s) * 110540;
  // Normalize so the longest side ≈ 800 units.
  const scale = 800 / Math.max(widthM, heightM, 1);
  const planeW = widthM * scale;
  const planeH = heightM * scale;
  // Z range from dataset metadata (real elevation range).
  const eMin = dataset.elevation_min ?? 0;
  const eMax = dataset.elevation_max ?? 1;
  const zSpanM = Math.max(eMax - eMin, 1);
  const zUnits = zSpanM * scale; // proportional to plane scale

  const geom = new THREE.PlaneGeometry(planeW, planeH, segW, segH);
  geom.rotateX(-Math.PI / 2);

  // Diffuse texture: the preview PNG.
  const tex = await loadTexture(previewBlobUrl);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;

  const mat = new THREE.MeshStandardMaterial({
    map: tex, metalness: 0.0, roughness: 0.95, side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geom, mat);
  mesh.position.y = 0;
  group.add(mesh);

  // Stash data so exaggeration changes can re-displace without re-fetching.
  group.userData.heightmapData = {
    mesh, geom, heights, segW: segW + 1, segH: segH + 1, zUnits,
  };
  applyHeightmapDisplacement(group.userData.heightmapData, exaggeration);
}

function applyHeightmapDisplacement(data, exaggeration) {
  const { geom, heights, zUnits } = data;
  const pos = geom.attributes.position;
  const k = (exaggeration ?? 1) * zUnits;
  for (let i = 0; i < pos.count; i++) {
    pos.setY(i, heights[i] * k);
  }
  pos.needsUpdate = true;
  geom.computeVertexNormals();
}

function sampleHeights(img, gridW, gridH) {
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0);
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  // Heightmap is single-channel grayscale; PNG decoder gives 4 channels.
  // PNG 16-bit will be normalized to 8-bit by canvas, which is fine for a
  // smoothed displacement preview (we're not building survey-grade terrain).
  const heights = new Float32Array(gridW * gridH);
  // Maintain plane vertex order: rows top→bottom, columns left→right.
  // PlaneGeometry default: y increases upward; after rotateX(-PI/2), the
  // first row in the buffer corresponds to z=-planeH/2 which is "north" if we
  // flip — let's just sample as-is and accept either orientation, it's a
  // visualization, not georeferencing.
  for (let j = 0; j < gridH; j++) {
    const ys = Math.round(((j) / (gridH - 1)) * (canvas.height - 1));
    for (let i = 0; i < gridW; i++) {
      const xs = Math.round(((i) / (gridW - 1)) * (canvas.width - 1));
      const idx = (ys * canvas.width + xs) * 4;
      // Normalize to [0, 1].
      heights[j * gridW + i] = data[idx] / 255;
    }
  }
  return heights;
}

function loadImage(src) {
  return new Promise((res, rej) => {
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => res(im);
    im.onerror = rej;
    im.src = src;
  });
}

function loadTexture(src) {
  return new Promise((res, rej) => {
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin("anonymous");
    loader.load(src, res, undefined, rej);
  });
}
